import React, { useCallback, useEffect, useState } from 'react'
import { Form, message, Modal, Col, Input, Select, Typography } from 'antd'
import type { TablePaginationConfig } from 'antd/es/table'
import { useAppContext } from '@/components/AppContext'
import { useParams, useSearchParams } from 'react-router-dom'
import withDefaultLayout from '@/hoc/WithDefaultLayout'
import AdminBreadcrumb from '@/ui/organisms/AdminBreadcrumb'
import _ from 'lodash'
import BatchView from '../../components/BatchView'
import BatchListPage from '@a4b/ui/src/modules/monetisation/puja/organisms/BatchListPage'
import { YODA_CDN_URL, PUJA_PRESIGN_URL, A4B_APPS } from '@/utils/constants'
import {
  Batch,
  MetaData,
  SKUFilters,
} from '@a4b/api/src/modules/Monetisation/Puja/types'
import CompletedPreviewModal from './CompletedPreviewModal'
import PreviewModal from './PreviewModal'
import UploadMediaModal from './UploadMediaModal'
import Error from '@/components/Error'
import CreateDeliveryOrder from './CreateDeliveryOrder'
import { deliveryOrderPayload, SANKALP_VIDEO_STATUS } from '../../utils/helper'

interface DataType {
  batch_id: string
  product_name: string
  store_name: string
}

interface DataType {
  key: React.Key
  name: string
  age: number
  address: string
}

const getPageBreadcrumbs = (TempleName: string, hubId: string) => [
  {
    path: '/monetisation/puja/puja-updates',
    breadcrumbName: 'Puja Updates',
  },
  {
    path: '/monetisation/puja',
    breadcrumbName: 'Hub List',
  },
  {
    path: `/monetisation/puja/hub/${hubId}`,
    breadcrumbName: TempleName,
  },
  {
    path: '/monetisation/puja/batches',
    breadcrumbName: 'Batch List',
  },
]

const BatchListScreen: React.FC = () => {
  const { networkInstance, monetisationRoles, languageState, app } = useAppContext()
  const monetisationPujaApi = app === A4B_APPS.SRIMANDIR ? networkInstance.clientWithHeaders.monetisationPujaApi : networkInstance.clientWithHeaders.monetisationAstroPujaApi

  let { hubId, templeId } = useParams()
  let [batchData, setBatchData] = useState<Batch[]>([])
  let [paginatedBatchData, setPaginatedBatchData] = useState<Batch[]>([])
  let [skuFilters, setSkuFilters] = useState<SKUFilters[]>([])
  const [showbatchView, setShowbatchView] = React.useState(false)
  const [isPreviewDone, setIsPreviewDone] = React.useState(false)
  const [showPreview, setShowPreview] = React.useState(false)
  const [currentTab, setCurrentTab] = React.useState<string>()
  const [totalRecord, setTotalRecord] = React.useState<number>(0)
  const [isComplete, setIsComplete] = React.useState(false)
  const [isCompletedPoojaService, setCompletedPoojaService] = React.useState(false)
  const [currentCompletedVideo, setCurrentCompletedVideo] = React.useState('')

  const [isPaginationChanged, setPaginationChanged] = React.useState(false)
  const [metaMap, setMetaMap] = React.useState<MetaData | undefined>()

  const [reducedItemNoCount, setReducedItemCount] = React.useState()
  const [currentBatchView, setCurrentBatchView] = React.useState<
    Batch | undefined
  >()
  const [isGenerateBatch, setIsGenerateBatch] = React.useState<boolean>(false)
  const [showMediaUploadModal, setShowMediaUploadModal] = React.useState<boolean>(false)
  const [showUploadButton, setShowUploadButton] = useState<boolean>(false)
  const [selectionType] = useState<string>('checkbox')
  const [loading, setLoading] = useState<boolean>(false)
  const [errorPage, setErrorPage] = React.useState<boolean>(false)
  const [showCreateOrderForm, setShowCreateOrderForm] = React.useState<boolean>(false)
  const [isDeliveryOrderCreated, setIsDeliveryOrderCreated] = React.useState<boolean>(false)
  const [selectedRow, setSelectedRow] = React.useState<any>()

  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    pageSizeOptions: [10, 20, 50, 100, 200],
    showSizeChanger: true,
  })

  const [batchForm] = Form.useForm()
  const [videoUrlForm] = Form.useForm()
  const [mediaUploadForm] = Form.useForm()
  const [createOrderForm] = Form.useForm()
  const [currentStep, setCurrentStep] = useState(1)
  const [searchParams] = useSearchParams()

  const [showEditVideoUrlForm, setShowEditVideoUrlForm] = useState<boolean>(false)
  const [videoFormObj, setVideoFormObj] = useState({
    batch_id: '',
    hash_code: '',
    media_type: 'youtube',
  })

  // State for multi-temple/multi-day video URL modal
  const [selectedModalTempleId, setSelectedModalTempleId] = useState<string | null>(null)
  const [selectedModalDay, setSelectedModalDay] = useState<number | null>(null)
  const [currentModalBatch, setCurrentModalBatch] = useState<Batch | null>(null)

  // Debug modal batch changes
  useEffect(() => {
    console.log('Modal batch changed:', currentModalBatch)
    console.log('Should show dropdowns:', shouldShowModalDropdowns(currentModalBatch))
  }, [currentModalBatch])

  // Sync form values when modal state changes
  useEffect(() => {
    if (showEditVideoUrlForm && currentModalBatch && selectedModalTempleId !== null) {
      const existingUrl = getVideoUrlForModalSelection(currentModalBatch, selectedModalTempleId, selectedModalDay)
      videoUrlForm.setFieldsValue({
        temple_id: selectedModalTempleId,
        day: selectedModalDay,
        existing_url: existingUrl
      })
    }
  }, [selectedModalTempleId, selectedModalDay, currentModalBatch, showEditVideoUrlForm, videoUrlForm])

  const getBatchList = useCallback(
    async (isForcefully?: boolean) => {
      if (!templeId || !monetisationRoles?.email || (isPaginationChanged && !isForcefully)) {
        return
      }
      try {
        const batchListList = await monetisationPujaApi.batchApi.getBatchList({
          store_id: templeId || '',
          user_email: monetisationRoles?.email || '',
          batch_status: isComplete ? 'completed' : '',
          limit: pagination.pageSize!,
          offset:
            Number((pagination?.current || 1) - 1) * Number(pagination.pageSize),
          ...((currentTab !== 'all_sku') && { inventory_id: currentTab }),
          sku_filter: !skuFilters?.length ? true : false
        })
        setPagination({ ...pagination, total: batchListList?.data?.data?.total || 10 })
        setMetaMap(batchListList?.data?.data?.meta_data)

        setPaginatedBatchData(batchListList?.data?.data?.batches)
        if (!batchListList?.data?.data?.batches) {
          message.warning('Batch list is empty')
          return
        }
        if (isForcefully) {
          message.success('Batch list refreshed successfully')
        }
        if (!skuFilters?.length)
          setSkuFilters(batchListList?.data?.data?.sku_filters)
        setBatchData(batchListList?.data?.data?.batches)
        setTotalRecord(batchListList?.data?.data?.batches.length)
        const reducedValue = batchListList?.data?.data?.batches.reduce(
          (acc: any, cur: Batch) => {
            if (cur.status)
              acc[cur.inventory_id] = acc[cur.inventory_id]
                ? acc[cur.inventory_id] + 1
                : 1
            return acc
          },
          {},
        )
        setReducedItemCount(reducedValue)
      } catch (error) {
        setErrorPage(true)
        console.log(error)
      }
    },
    [templeId, monetisationRoles?.email, isPaginationChanged, monetisationPujaApi.batchApi, isComplete, pagination?.current, pagination?.pageSize, currentTab],
  )

  useEffect(() => {
    getBatchList()
  }, [getBatchList, isComplete])


  // useEffect(() => {
  //   if (currentBatchView) {
  //     const config = localStorage.getItem(currentBatchView?.batch_id)
  //     const parsedConfig = JSON.parse(config || '{}')
  //     if (currentBatchView.product_type === 'pooja') {
  //       if (parsedConfig?.namelistDownload) {
  //         setCurrentStep(3)
  //       } else if (parsedConfig?.orderMediaUpload) {
  //         setCurrentStep(2)
  //       } else {
  //         setCurrentStep(1)
  //       }
  //     }
  //     if (currentBatchView.product_type === 'item') {
  //       if (parsedConfig?.namelistDownload) {
  //         setCurrentStep(2)
  //       } else {
  //         setCurrentStep(1)
  //       }
  //     }
  //     if (currentBatchView.product_type === 'prayer') {
  //       if (parsedConfig?.namelistDownload) {
  //         setCurrentStep(2)
  //       } else if (parsedConfig?.prayerDownload) {
  //         setCurrentStep(1)
  //       }
  //     }
  //   }
  // }, [currentBatchView])

  const generateBatch = async () => {
    setIsGenerateBatch(true)
    try {
      await monetisationPujaApi.batchApi.generateBatches({
        store_id: templeId || '',
        user_email: monetisationRoles?.email || '',
      })
      message.success({
        content: 'Batches generated successfully',
        duration: 3,
      })
      getBatchList()
      setIsGenerateBatch(false)
    } catch (error) {
      message.error({
        content: 'Error while generating batch',
        duration: 3,
      })
    }
  }

  // const getNameTranslation = async (
  //   batch_id: string,
  //   nameList: string[],
  //   callback: any,
  // ) => {
  //   const hindiTranslation = await monetisationPujaApi.batchApi.translateStringsToHindi(nameList)
  //   callback(hindiTranslation?.data.data?.output?.strings.map((item: string, index: number) => `${item} - [ ${nameList[index]} ]`))
  // }

  const onViewBatchClick = async (batchCode: string, batch?: Batch) => {
    if (batch && batch?.product_type !== 'pooja') {
      message.warning('Invalid product type')
      return
    }
    let translatedBatch = null
    const batchData = await fetchBatch(batchCode)
    handleChangeStep(2)
    if (batchData) {
      // let nameList: string[][] = batchData.pooja_member_data?.map((item: PoojaMemberData) => {
      //   const names = item?.members?.map((member: Member) => member?.full_name)
      //   return names?.length ? names : [""]
      // })
      // try {
      //   const translationResponse = await monetisationPujaApi.batchApi.translatePujaTextToEnglish(
      //     batchData.batch_id,
      //     nameList,
      //   )

      //   translatedBatch = {
      //     ...batchData,
      //     pooja_member_data: batchData?.pooja_member_data?.map((pujaMember: PoojaMemberData, poojaMembersIndex: number) => {
      //       return {
      //         ...pujaMember, members: pujaMember?.members?.map((members: Member, membersIndex: number) => {
      //           return { ...members, full_name: translationResponse?.data?.[poojaMembersIndex]?.[membersIndex] }
      //         })
      //       }
      //     }),

      //   }
      //   message.success({ content: 'Translation fetched!' })
      // } catch (error) {
      //   message.error({ content: "Translation failed" })
      // }

      const batchContent = translatedBatch ?? batchData
      setCurrentBatchView(batchContent)


      setShowbatchView(true)
      if (batchData?.video_tagging_status === SANKALP_VIDEO_STATUS.PROCESSED
        || batchData?.video_tagging_status === SANKALP_VIDEO_STATUS.PROCESSING
        || batchData?.video_tagging_status === SANKALP_VIDEO_STATUS.NOT_APPLICABLE
      ) {
        setCurrentStep(3)
      } else {
        setCurrentStep(1)
      }
      // Since we're not using this case, commenting the block. Later we should remove this else case

      // const inventoryById = await monetisationPujaApi.storesApi.getInventoryById(batchData?.inventory_id)
      // getNameTranslation(
      //   batchData.batch_id,
      //   batchData.offering_names,
      //   (result: string[]) => {
      //     message.destroy('trans')
      //     setCurrentBatchView({
      //       ...batchData,
      //       offering_names: result || [''],
      //       intro_url: inventoryById?.data?.data?.IntroVideoUrl,
      //       outro_url: inventoryById?.data?.data?.OutroVideoUrl
      //     })
      //     setShowbatchView(true)
      //   },
      // )        
    } else {
      console.error('Batch data is invalid or not fetched');
    }
  }

  const closeHandler = () => {
    setShowbatchView(false)
    setCurrentBatchView(undefined)
    setCurrentStep(1)
  }

  const handleFormSubmit = (context?: { executionId: string }) => {
    if (!currentBatchView) {
      return
    }

    console.log('Form submit context:', context)

    batchForm
      .validateFields()
      .then(async values => {
        const batchFormData = batchForm.getFieldsValue()
        const formData = {
          hash_code: currentBatchView?.hash_code,
          video_url: batchFormData?.videoType === "youtube" ? batchFormData?.video_url : batchFormData?.video_url?.[0],
          media_type: batchFormData?.videoType,
          // Add execution_id for multi-temple/multi-day batches
          ...(context?.executionId && { execution_id: context.executionId }),
        }

        console.log('Submitting batch with payload:', formData)

        try {
          await monetisationPujaApi.batchApi.completeBatch(
            currentBatchView.batch_id,
            formData,
          )
          message.success({
            content: `Batch updated successfully${context?.executionId ? ` for execution ID: ${context.executionId}` : ''}`,
            duration: 3,
          })
          setShowbatchView(false)
          batchForm.resetFields()
          getBatchList()
        } catch (error) {
          message.error({
            content: 'Error while updating batch',
            duration: 3,
          })
          console.log(error)
        }
      })
      .catch(error => {
        console.log(error)
      })
  }

  // const initiatePreviewStatus = useCallback(() => {
  //   if (!currentBatchView?.batch_id) {
  //     return
  //   }
  //   const config = localStorage.getItem(currentBatchView.batch_id)
  //   const parsedConfig = JSON.parse(config || '{}')
  //   if (parsedConfig?.isPreviewDone) {
  //     setIsPreviewDone(true)
  //   }

  //   localStorage.setItem(
  //     currentBatchView.batch_id,
  //     JSON.stringify({ ...parsedConfig }),
  //   )
  // }, [currentBatchView?.batch_id])

  // useEffect(() => {
  //   initiatePreviewStatus()
  // }, [initiatePreviewStatus])

  const updatePreviewStatus = (previewStatus: boolean) => {
    if (!currentBatchView?.batch_id) {
      return
    }
    const config = localStorage.getItem(currentBatchView.batch_id)
    const parsedConfig = JSON.parse(config || '{}')

    localStorage.setItem(
      currentBatchView.batch_id,
      JSON.stringify({ ...parsedConfig, isPreviewDone: previewStatus }),
    )
  }
  const resetPreview = () => {
    if (!currentBatchView?.batch_id) {
      return
    }

    const config = localStorage.getItem(currentBatchView.batch_id)
    const parsedConfig = JSON.parse(config || '{}')
    localStorage.setItem(
      currentBatchView.batch_id,
      JSON.stringify({ ...parsedConfig, isPreviewDone: false }),
    )
  }

  const openPreviewModal = () => {
    handleOpenPreviewModal()
  }


  const onTabChange = (activeKey: string) => {
    setCurrentTab(activeKey)
  }

  const handleCloseModal = () => {
    setShowPreview(false)
  }
  const handleOpenPreviewModal = () => {
    setShowPreview(true)
  }
  const handleQcDone = () => {
    handleCloseModal()
    setIsPreviewDone(true)
    updatePreviewStatus(true)
  }
  const getVideoUrl = () => {
    return batchForm.getFieldsValue().videoType === "youtube" ? batchForm.getFieldsValue().video_url : batchForm.getFieldsValue().video_url?.[0]
  }
  const onFormChange = () => {
    resetPreview()
    setIsPreviewDone(false)
  }

  const onCompleteChange = () => {
    setIsComplete(!isComplete)
  }
  const handleCompletedVideoPreview = (videoUrl: string, video_type: string, record: any) => {
    setCurrentCompletedVideo(videoUrl)
    setCompletedPoojaService(video_type === "youtube" ? true : false)
  }

  const handleTableChange = (pagination: TablePaginationConfig) => {
    setPagination({
      ...pagination,
    })
  }

  const rowSelectionChange = (selectedRowKeys: React.Key[], selectedRows: DataType[]) => {
    setShowUploadButton(selectedRows?.length > 0)
    setRowSelection({
      ...rowSelection,
      selectedRows: [...selectedRows],
    })
  }

  const [rowSelection, setRowSelection] = useState<any>({
    selectedRows: [],
    onChange: rowSelectionChange
  })

  const handleClickUploadMedia = () => {
    setShowMediaUploadModal(true)
  }

  const handleUploadMediaMultiple = async () => {
    setLoading(true)
    try {
      await mediaUploadForm.validateFields()
      const mediaUploadFormData = mediaUploadForm.getFieldsValue()
      let payload = mediaUploadFormData?.order_media?.map((item: any) => ({
        type: mediaUploadFormData?.order_media_type,
        media_url: item,
      }))
      const formData = {
        order_media: payload,
      }

      const setDelay = (ms = 100) => new Promise((r) => setTimeout(r, ms))
      let failedBatches = []

      for (const row of rowSelection?.selectedRows) {
        try {
          await setDelay()
          await monetisationPujaApi.batchApi.uploadOrderMedia(row?.batch_id, formData)
          message.success({
            content: `Order Media updated for - ${row?.batch_code}`,
            duration: 3,
          })
        } catch (error) {
          failedBatches.push(row?.batch_id);
          message.error({
            content: `Error while updating batch - ${row?.batch_code}`,
            duration: 2,
          })
        }
      }
      setLoading(false)
      setShowMediaUploadModal(false)
      setRowSelection({ ...rowSelection, selectedRowKeys: [] })
      setShowUploadButton(false)
    } catch (error) {
      console.log(error)
    }
  }

  const handleCancelUploadMedia = () => {
    setShowMediaUploadModal(false)
  }

  const handleClickCreateDelivery = (record: any) => {
    createOrderForm?.resetFields()
    setSelectedRow(record)
    setShowCreateOrderForm(true)
  }

  const createDelivery = () => {
    createOrderForm
      .validateFields()
      .then(async (values: any) => {
        const payload = deliveryOrderPayload(values, selectedRow?.batch_id)
        try {
          const successDelivery = await monetisationPujaApi.batchApi.createDeliveryOrder(payload)
          setIsDeliveryOrderCreated(true)
          setShowCreateOrderForm(false)
          message.success(successDelivery?.data?.data, 2)
          getBatchList()
        } catch (error) {
          message.error('Error while creating Delivery Order Creation!', 2)
        }
      })
      .catch(error => {
        console.log('Create delivery order error')
      })
  }


  const handleChangeStep = (step: number) => {
    setCurrentStep(step)
  }

  const fetchBatch = useCallback(async (batchCode?: string) => {
    try {
      const batchData = await monetisationPujaApi.batchApi.getBatchByCode(batchCode ?? currentBatchView?.batch_code!)
      message.success('Fetched latest batch data', 2)
      return batchData?.data?.data
    } catch (error) {
      message.error('Error while Fetching latest batch data', 2)
      return null
    }
  }, [currentBatchView?.batch_code, monetisationPujaApi.batchApi])

  const updateLatestBatchVideo = (batch: any) => {


    setCurrentModalBatch(batchData as any)
    setShowEditVideoUrlForm(true)

    if (batch?.multi_temple || batch?.multi_day) {

      // Initialize with first temple and day
      const firstTemple = batch?.temples_data?.[0]
      const firstDay = firstTemple?.schedules?.[0]?.day ?? null

      setSelectedModalTempleId(firstTemple?.temple_id || null)
      setSelectedModalDay(firstDay)

      // Set existing URL from the selected temple/day combination
      const existingUrl = getVideoUrlForModalSelection(batch, firstTemple?.temple_id, firstDay)

      videoUrlForm.setFieldsValue({
        batch_id: batch?.batch_id,
        existing_url: existingUrl,
        temple_id: firstTemple?.temple_id,
        day: firstDay
      })
    } else {
      console.log('Initializing single temple/day modal')
      // Regular single temple/day batch
      videoUrlForm.setFieldsValue({
        batch_id: batch?.batch_id,
        existing_url: batch?.video_url
      })
    }

    setVideoFormObj({
      ...videoFormObj,
      batch_id: batch.batch_id,
      hash_code: batch.hash_code,
    })
  }

  // Helper function to check if we should show dropdowns
  const shouldShowModalDropdowns = (batch: any) => {
    return batch?.multi_temple || batch?.multi_day
  }

  // Helper function to get video URL for specific temple/day combination
  const getVideoUrlForModalSelection = (batch: any, templeId: string | null, day: number | null) => {
    if (!batch?.temples_data || !templeId || day === null) {
      return batch?.video_url || 'No video URL available'
    }

    const temple = batch.temples_data.find((t: any) => t.temple_id === templeId)
    if (!temple) return 'Temple not found'

    const schedule = temple?.schedules?.find((s: any) => s.day === day)
    if (!schedule) return 'Schedule not found for selected day'

    return schedule?.video_url || 'No video URL set for this temple/day'
  }

  // Handler for temple change in modal
  const handleModalTempleChange = (templeId: string) => {
    console.log('Temple changed to:', templeId)
    console.log('Available temples:', currentModalBatch?.temples_data)

    setSelectedModalTempleId(templeId)

    const temple = currentModalBatch?.temples_data?.find((t: any) => t.temple_id === templeId)
    const firstDay = temple?.schedules?.[0]?.day ?? null

    console.log('Found temple:', temple)
    console.log('First day:', firstDay)

    setSelectedModalDay(firstDay)

    const existingUrl = getVideoUrlForModalSelection(currentModalBatch, templeId, firstDay)
    console.log('Existing URL for selection:', existingUrl)

    videoUrlForm.setFieldsValue({
      temple_id: templeId,
      day: firstDay,
      existing_url: existingUrl,
      video_url: '' // Reset new video URL when selection changes
    })
  }

  // Handler for day change in modal
  const handleModalDayChange = (day: number) => {
    console.log('Day changed to:', day)
    console.log('Selected temple ID:', selectedModalTempleId)

    setSelectedModalDay(day)

    const existingUrl = getVideoUrlForModalSelection(currentModalBatch, selectedModalTempleId, day)
    console.log('Existing URL for day selection:', existingUrl)

    videoUrlForm.setFieldsValue({
      day: day,
      existing_url: existingUrl,
      video_url: '' // Reset new video URL when selection changes
    })
  }

  const handleFormVideoUrlSubmit = () => {
    videoUrlForm
      .validateFields()
      .then(async values => {
        const videoUrlFormData = videoUrlForm.getFieldsValue()
        console.log(videoUrlFormData)

        // Get execution_id for multi-temple/multi-day batches
        let executionId = null
        if (currentModalBatch?.multi_temple || currentModalBatch?.multi_day) {
          const temple = currentModalBatch?.temples_data?.find((t: any) => t.temple_id === selectedModalTempleId)
          const schedule = temple?.schedules?.find((s: any) => s.day === selectedModalDay)
          executionId = schedule?.id || null
        }

        const formData = {
          hash_code: videoFormObj?.hash_code,
          video_url: videoUrlFormData?.video_url,
          media_type: videoFormObj?.media_type,
          ...(executionId && { execution_id: executionId }),
        }

        console.log('Submitting video URL with payload:', formData)

        try {
          await monetisationPujaApi.batchApi.completeBatch(
            videoFormObj.batch_id,
            formData,
          )
          message.success({
            content: `Batch updated successfully${executionId ? ` for execution ID: ${executionId}` : ''}`,
            duration: 3,
          })
          videoUrlForm?.resetFields()
          setShowEditVideoUrlForm(false)
          // Reset modal state
          setSelectedModalTempleId(null)
          setSelectedModalDay(null)
          setCurrentModalBatch(null)
          getBatchList()
        } catch (error) {
          message.error({
            content: 'Error while updating batch',
            duration: 3,
          })
          console.log(error)
        }
      })
      .catch(error => {
        console.log(error)
      })
  }

  const onSearch = async (criteria: string) => {
    const key = "search"
    message.open({
      key,
      type: 'loading',
      content: 'Searching...',
    });
    if (criteria) {
      try {

        const batchResponse = await monetisationPujaApi?.batchApi?.searchBatches(criteria?.trim())
        message.destroy(key);
        if (batchResponse?.data?.error) {
          message.error(batchResponse?.data?.error?.message);
        } else {
          setPaginatedBatchData([batchResponse?.data?.data])
        }
      } catch (error) {
        message.error(`Error occured:${error}`);
      }
    } else {
      getBatchList()
    }
  }

  return (
    <div style={{ padding: '0 40px' }}>
      {
        errorPage ?
          <Error text='Error While fetching Batch List!' /> :
          <>
            <AdminBreadcrumb
              routes={getPageBreadcrumbs(searchParams.get('name') || '', hubId || '')}
            />
            <BatchListPage
              meta_data={metaMap}
              onSearch={onSearch}
              itemList={paginatedBatchData || []}
              onViewBatchClick={onViewBatchClick}
              generateBatch={generateBatch}
              handleTableChange={handleTableChange}
              pagination={pagination}
              skuFilters={skuFilters!}
              onTabChange={onTabChange}
              isComplete={isComplete}
              onCompleteChange={onCompleteChange}
              reducedItemCount={reducedItemNoCount}
              batchCount={batchData.length}
              handleCompletedVideoPreview={handleCompletedVideoPreview}
              languageSelected={languageState?.monetisationLanguage}
              isGenerateBatch={isGenerateBatch}
              handleClickUploadMedia={handleClickUploadMedia}
              showUploadButton={showUploadButton}
              selectionType={selectionType}
              rowSelection={rowSelection}
              handleCreateDelivery={handleClickCreateDelivery}
              updateLatestBatchVideo={updateLatestBatchVideo}
              isLoading={loading}
            />
          </>
      }
      <Modal
        title="Update Batch video url"
        visible={showEditVideoUrlForm}
        onOk={handleFormVideoUrlSubmit}
        onCancel={() => {
          videoUrlForm?.resetFields()
          setShowEditVideoUrlForm(false)
          // Reset modal state
          setSelectedModalTempleId(null)
          setSelectedModalDay(null)
          setCurrentModalBatch(null)
        }}
        okText='Submit'
        cancelText='Cancel'
        zIndex={2000}
        className='video-url-edit-form'
        width={600}
        destroyOnClose={true}
        maskClosable={false}
      >
        <Form
          form={videoUrlForm}
          layout='vertical'
          name='update_batch_video'
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          initialValues={{ remember: true }}
        >
          <Col span={24}>
            <Form.Item
              label='Batch ID'
              name='batch_id'
              style={{ width: '100%' }}
            >
              <Input placeholder='Batch ID' readOnly disabled />
            </Form.Item>

            {/* Show temple and day dropdowns for multi-temple or multi-day batches */}
            {shouldShowModalDropdowns(currentModalBatch) && (
              <div style={{ display: 'flex', gap: '16px' }}>
                {currentModalBatch?.multi_temple && (
                  <Form.Item
                    label='Select Temple'
                    name='temple_id'
                    rules={[{ required: true, message: 'Please select a temple' }]}
                    style={{ width: '100%' }}
                  >
                    <Select
                      placeholder="Select a temple"
                      onChange={handleModalTempleChange}
                      style={{ width: '100%' }}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) => {
                        const children: any = option?.children;
                        if (typeof children === 'string') {
                          return children?.toLowerCase().includes(input.toLowerCase());
                        }
                        return true; // Default to showing the option if children is not a string
                      }}
                      getPopupContainer={(trigger) => trigger.parentNode}
                      dropdownStyle={{
                        zIndex: 3000,
                        maxHeight: 200,
                        overflow: 'auto'
                      }}
                      notFoundContent={
                        currentModalBatch?.temples_data?.length === 0 ?
                          "No temples available" :
                          "No matching temples"
                      }
                    >
                      {currentModalBatch?.temples_data && currentModalBatch?.temples_data?.length > 0 ? (
                        currentModalBatch.temples_data.map((temple: any) => (
                          <Select.Option
                            key={temple.temple_id}
                            value={temple.temple_id}
                            title={temple.temple_name} // Tooltip on hover
                          >
                            {temple.temple_name || `Temple ${temple.temple_id}`}
                          </Select.Option>
                        ))
                      ) : (
                        <Select.Option disabled value="">
                          No temples available
                        </Select.Option>
                      )}
                    </Select>
                  </Form.Item>
                )}

                {currentModalBatch?.multi_day && (
                  <Form.Item
                    label='Select Day'
                    name='day'
                    rules={[{ required: true, message: 'Please select a day' }]}
                    style={{ width: '100%' }}
                  >
                    <Select
                      placeholder="Select a day"
                      onChange={handleModalDayChange}
                      style={{ width: '100%' }}
                      allowClear
                      disabled={!selectedModalTempleId} // Disable if no temple selected
                      getPopupContainer={(trigger) => trigger.parentNode}
                      dropdownStyle={{
                        zIndex: 3000,
                        maxHeight: 200,
                        overflow: 'auto'
                      }}
                      notFoundContent={
                        !selectedModalTempleId ?
                          "Please select a temple first" :
                          "No days available"
                      }
                    >
                      {(() => {
                        if (!currentModalBatch) {
                          return (
                            <Select.Option disabled value="">
                              Loading...
                            </Select.Option>
                          );
                        }

                        const selectedTemple = selectedModalTempleId
                          ? currentModalBatch.temples_data?.find((t: any) => t.temple_id === selectedModalTempleId)
                          : null;

                        const hasSchedules = selectedTemple?.schedules && selectedTemple?.schedules?.length > 0;

                        if (hasSchedules) {
                          return selectedTemple?.schedules?.map((schedule: any) => (
                            <Select.Option
                              key={schedule.day}
                              value={schedule.day}
                              title={`Day ${schedule.day} - ${schedule.process_date || 'N/A'}`}
                            >
                              {`Day ${schedule.day} (${schedule.process_date || 'N/A'})`}
                            </Select.Option>
                          ));
                        }

                        return (
                          <Select.Option disabled value="">
                            {!selectedModalTempleId ? "Select temple first" : "No days available"}
                          </Select.Option>
                        );
                      })()}
                    </Select>
                  </Form.Item>
                )}
              </div>
            )}

            <Form.Item
              label='Current media URL'
              name='existing_url'
              style={{ width: '100%' }}
            >
              <Input.TextArea
                placeholder='Current media URL'
                disabled
                autoSize={{ minRows: 1, maxRows: 4 }}
                style={{ resize: 'none' }}
              />
            </Form.Item>

            <Form.Item
              label='New media URL'
              name='video_url'
              style={{ width: '100%' }}
              rules={[
                {
                  required: true,
                  message: 'Please input media URL',
                },
                {
                  type: 'url',
                  message: 'Please input a valid URL (must start with http:// or https://)',
                },
                {
                  validator: (_, value) => {
                    if (value && !value.match(/^https?:\/\/.+/)) {
                      return Promise.reject('URL must start with http:// or https://');
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input.TextArea
                placeholder='Enter new media URL (e.g., https://example.com/video.mp4)'
                autoSize={{ minRows: 1, maxRows: 4 }}
                style={{ resize: 'none' }}
              />
            </Form.Item>
          </Col>
        </Form>
      </Modal>
      <BatchView
        visible={showbatchView}
        batchData={currentBatchView}
        onClose={closeHandler}
        currentStep={currentStep}
        cdnUrl={YODA_CDN_URL}
        preSignUrl={PUJA_PRESIGN_URL}
        form={batchForm}
        handleFormSubmit={handleFormSubmit}
        isPreviewDone={isPreviewDone}
        openPreview={openPreviewModal}
        onFormChange={onFormChange}
        fetchBatch={onViewBatchClick}
        handleChangeStep={handleChangeStep}
      />
      <PreviewModal
        isPujaService={currentBatchView?.product_type === "pooja"}
        visible={showPreview}
        closeModal={handleCloseModal}
        handleDoneQc={handleQcDone}
        videoUrl={getVideoUrl()}
        batchData={currentBatchView}
        videoType={batchForm.getFieldsValue().videoType}
      />
      {
        <CompletedPreviewModal
          title={currentCompletedVideo?.split('/')?.pop() || ''}
          visible={!!currentCompletedVideo}
          closeModal={() => setCurrentCompletedVideo('')}
          handleDoneQc={() => { }}
          videoUrl={currentCompletedVideo}
          batchData={currentBatchView}
          okTitle={''}
          closeTitle={'Close'}
          isPujaService={isCompletedPoojaService}
        />
      }
      <UploadMediaModal
        visibleMediaUpload={showMediaUploadModal}
        handleOk={handleUploadMediaMultiple}
        handleCancel={handleCancelUploadMedia}
        mediaUploadForm={mediaUploadForm}
      />
      <CreateDeliveryOrder
        showItemCreation={showCreateOrderForm}
        closeItemCreation={() => setShowCreateOrderForm(false)}
        form={createOrderForm}
        initialValues={null}
        createDelivery={createDelivery}
      />
    </div>
  )
}

export default withDefaultLayout(BatchListScreen)
